package com.heelion.farmafreelance.ui

import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import farmafreelance.composeapp.generated.resources.*
import com.heelion.farmafreelance.models.RecuperoAccessoRequest
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun LoginScreen(
    onLoginSuccess: (String, String) -> Unit,
    onRegisterClick: () -> Unit,
    onSettingsClick: () -> Unit = {},
    currentLanguage: String = "it",
    onLanguageChange: (String) -> Unit = {},
    isLoading: Boolean = false,
    errorMessage: String? = null,
    rememberMe: Boolean = false,
    onRememberMeChange: (Boolean) -> Unit = {},
    initialEmail: String = "",
    onForgotPasswordClick: (emailScritta: String) -> Unit = {}
) {
    var email by remember { mutableStateOf(initialEmail) }
    var password by remember { mutableStateOf("") }
    
    // Forza aggiornamento email se cambia initialEmail (es. quando carica dallo storage)
    LaunchedEffect(initialEmail) {
        if (email.isBlank()) email = initialEmail
    }

    var emailError by remember { mutableStateOf<String?>(null) }
    // L'email si controlla quando si lascia il campo o si preme Accedi, non a ogni lettera.
    var emailToccata by remember { mutableStateOf(false) }
    val scrollState = rememberScrollState()

    val invalidEmailMsg = stringResource(Res.string.invalid_email)

    fun validateEmail(mail: String): Boolean {
        val emailRegex = "^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\\.[a-z]{2,}$".toRegex()
        return emailRegex.matches(mail.trim())
    }

    Box(modifier = Modifier.fillMaxSize()) {
        // Icona Impostazioni (Ingranaggio) - Apre direttamente il dettaglio
        IconButton(
            onClick = onSettingsClick,
            modifier = Modifier
                .statusBarsPadding()
                .padding(16.dp)
                .align(Alignment.TopEnd)
        ) {
            Icon(
                imageVector = AppIcons.Settings,
                contentDescription = "Settings",
                tint = MaterialTheme.colorScheme.primary,
                modifier = Modifier.size(28.dp)
            )
        }

        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(24.dp)
                .verticalScroll(scrollState),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            // Logo
            Image(
                painter = painterResource(Res.drawable.farmafreelance_logo),
                contentDescription = "FarmaFreelance Logo",
                modifier = Modifier.height(80.dp).padding(bottom = 16.dp)
            )

            Text(
                text = stringResource(Res.string.welcome_back),
                style = MaterialTheme.typography.headlineMedium,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.primary
            )
            Text(
                text = stringResource(Res.string.login_subtitle),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )

            Spacer(modifier = Modifier.height(32.dp))

            // Email
            OutlinedTextField(
                value = email,
                onValueChange = { 
                    email = it
                    emailError = null
                },
                label = { Text(stringResource(Res.string.email_label)) },
                placeholder = { Text(stringResource(Res.string.email_placeholder)) },
                modifier = Modifier.fillMaxWidth().onFocusChanged { stato ->
                    if (stato.isFocused) emailToccata = true
                    else if (emailToccata && email.isNotBlank() && !validateEmail(email)) emailError = invalidEmailMsg
                },
                isError = emailError != null,
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
                shape = RoundedCornerShape(12.dp)
            )
            if (emailError != null) {
                Text(
                    text = stringResource(Res.string.invalid_email),
                    color = MaterialTheme.colorScheme.error,
                    style = MaterialTheme.typography.labelSmall,
                    modifier = Modifier.align(Alignment.Start).padding(start = 8.dp)
                )
            }

            Spacer(modifier = Modifier.height(16.dp))

            // Password
            CampoPassword(
                value = password,
                onValueChange = { password = it },
                label = { Text(stringResource(Res.string.password_label)) },
                modifier = Modifier.fillMaxWidth()
            )

            Spacer(modifier = Modifier.height(4.dp))

            TextButton(
                onClick = { onForgotPasswordClick(email) },
                modifier = Modifier.align(Alignment.End)
            ) {
                Text("Password dimenticata?", style = MaterialTheme.typography.bodySmall)
            }

            Spacer(modifier = Modifier.height(4.dp))

            // Ricordami
            Row(
                modifier = Modifier.fillMaxWidth().padding(horizontal = 4.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Checkbox(
                    checked = rememberMe,
                    onCheckedChange = onRememberMeChange,
                    colors = CheckboxDefaults.colors(checkedColor = MaterialTheme.colorScheme.primary)
                )
                Text(
                    text = stringResource(Res.string.remember_me_label),
                    style = MaterialTheme.typography.bodySmall,
                    modifier = Modifier.padding(start = 4.dp)
                )
            }

            Spacer(modifier = Modifier.height(16.dp))

            if (errorMessage != null) {
                Text(
                    text = errorMessage,
                    color = MaterialTheme.colorScheme.error,
                    style = MaterialTheme.typography.bodySmall,
                    modifier = Modifier.padding(bottom = 8.dp)
                )
            }

            Button(
                onClick = {
                    if (!validateEmail(email)) emailError = invalidEmailMsg
                    else if (password.isNotBlank()) onLoginSuccess(email.trim(), password)
                },
                modifier = Modifier.fillMaxWidth().height(56.dp),
                shape = RoundedCornerShape(12.dp),
                enabled = !isLoading && email.isNotBlank() && password.isNotBlank(),
                colors = ButtonDefaults.buttonColors(
                    containerColor = MaterialTheme.colorScheme.primary,
                    contentColor = Color.White
                )
            ) {
                if (isLoading) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(24.dp),
                        color = Color.White,
                        strokeWidth = 2.dp
                    )
                } else {
                    Text(stringResource(Res.string.login_button), fontWeight = FontWeight.Bold)
                }
            }

            Spacer(modifier = Modifier.height(16.dp))

            OutlinedButton(
                onClick = onRegisterClick,
                modifier = Modifier.fillMaxWidth().height(56.dp),
                shape = RoundedCornerShape(12.dp),
                border = androidx.compose.foundation.BorderStroke(2.dp, MaterialTheme.colorScheme.primary)
            ) {
                Text(
                    stringResource(Res.string.no_account_button),
                    style = MaterialTheme.typography.bodyLarge,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.primary
                )
            }
        }
    }
}

/**
 * Reset password in due passi: 1) l'utente inserisce l'email e riceve un codice a 6 cifre via
 * email (valido 15 minuti); 2) inserisce il codice + la nuova password. Il server risponde sempre
 * con lo stesso messaggio generico al passo 1 indipendentemente dal fatto che l'email esista, per
 * non rivelare quali indirizzi sono registrati.
 *
 * Chi non ricorda l'email può cercarla con codice fiscale o partita IVA: il server la mostra
 * mascherata, quanto basta per riconoscerla e riscriverla.
 */
@Composable
fun ForgotPasswordDialog(viewModel: AuthViewModel, initialEmail: String = "", onDismiss: () -> Unit) {
    var step by remember { mutableStateOf(1) }
    // Parte dall'email già scritta nel login: niente da riscrivere (e niente suggerimenti sbagliati).
    var email by remember { mutableStateOf(initialEmail.trim()) }
    var cercaEmail by remember { mutableStateOf(false) }
    var codiceIscrizione by remember { mutableStateOf("") }
    var emailTrovate by remember { mutableStateOf<List<String>>(emptyList()) }
    // "Ho perso l'accesso": né password né email, serve la verifica dell'amministratore.
    var accessoPerso by remember { mutableStateOf(false) }
    var code by remember { mutableStateOf("") }
    var newPassword by remember { mutableStateOf("") }
    var confirmPassword by remember { mutableStateOf("") }
    var infoMessage by remember { mutableStateOf<String?>(null) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var isLoading by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    if (accessoPerso) {
        RecuperoAccessoDialog(viewModel, codiceIniziale = codiceIscrizione, onIndietro = { accessoPerso = false }, onChiudi = onDismiss)
    } else Dialog(onDismissRequest = onDismiss) {
        Card(shape = RoundedCornerShape(16.dp), modifier = Modifier.padding(8.dp)) {
            Column(modifier = Modifier.padding(24.dp).width(320.dp)) {
                Text(
                    text = "Password dimenticata",
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold
                )
                Spacer(modifier = Modifier.height(16.dp))

                if (step == 1 && cercaEmail) {
                    Text(
                        "Scrivi il codice fiscale (farmacisti) o la partita IVA (farmacie) dell'iscrizione: ti mostriamo l'email in parte nascosta.",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    Spacer(modifier = Modifier.height(12.dp))
                    OutlinedTextField(
                        value = codiceIscrizione,
                        onValueChange = { codiceIscrizione = it },
                        label = { Text("Codice fiscale o partita IVA") },
                        singleLine = true,
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp)
                    )
                    if (emailTrovate.isNotEmpty()) {
                        Spacer(modifier = Modifier.height(12.dp))
                        Text("Ti sei iscritto con:", style = MaterialTheme.typography.bodySmall)
                        emailTrovate.forEach {
                            Text(it, style = MaterialTheme.typography.bodyLarge, fontWeight = FontWeight.Bold)
                        }
                        Text(
                            "Riscrivila per intero per ricevere il codice.",
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                    TextButton(onClick = {
                        cercaEmail = false
                        errorMessage = null
                        infoMessage = null
                    }) { Text("Torna all'email") }
                    TextButton(onClick = { accessoPerso = true }) { Text("Non hai più accesso a quell'email?") }
                } else if (step == 1) {
                    Text(
                        "Inserisci l'email con cui ti sei registrato: ti mandiamo un codice per reimpostare la password.",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    Spacer(modifier = Modifier.height(12.dp))
                    OutlinedTextField(
                        value = email,
                        onValueChange = { email = it },
                        label = { Text("Email") },
                        singleLine = true,
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp)
                    )
                    TextButton(onClick = {
                        cercaEmail = true
                        errorMessage = null
                        infoMessage = null
                    }) { Text("Non ricordi l'email?") }
                } else {
                    Text(
                        // L'indirizzo in chiaro fa notare subito se il codice è andato a un'altra email.
                        "Abbiamo mandato il codice a ${email.trim()}: inserisci il codice ricevuto e la nuova password.",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    Spacer(modifier = Modifier.height(12.dp))
                    OutlinedTextField(
                        value = code,
                        onValueChange = { code = it },
                        label = { Text("Codice a 6 cifre") },
                        singleLine = true,
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp)
                    )
                    Spacer(modifier = Modifier.height(8.dp))
                    CampoPassword(
                        value = newPassword,
                        onValueChange = { newPassword = it },
                        label = { Text("Nuova password") },
                        modifier = Modifier.fillMaxWidth()
                    )
                    Spacer(modifier = Modifier.height(8.dp))
                    CampoPassword(
                        value = confirmPassword,
                        onValueChange = { confirmPassword = it },
                        label = { Text("Conferma password") },
                        modifier = Modifier.fillMaxWidth()
                    )
                }

                if (errorMessage != null) {
                    Spacer(modifier = Modifier.height(8.dp))
                    Text(errorMessage!!, color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodySmall)
                }
                if (infoMessage != null) {
                    Spacer(modifier = Modifier.height(8.dp))
                    Text(infoMessage!!, color = MaterialTheme.colorScheme.primary, style = MaterialTheme.typography.bodySmall)
                }

                Spacer(modifier = Modifier.height(20.dp))

                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                    TextButton(onClick = onDismiss) { Text("Annulla") }
                    Spacer(modifier = Modifier.width(8.dp))
                    Button(
                        enabled = !isLoading,
                        onClick = {
                            errorMessage = null
                            infoMessage = null
                            if (step == 1 && cercaEmail) {
                                if (codiceIscrizione.isBlank()) {
                                    errorMessage = "Scrivi il codice fiscale o la partita IVA."
                                    return@Button
                                }
                                isLoading = true
                                scope.launch {
                                    val resp = viewModel.recuperaEmail(codiceIscrizione)
                                    isLoading = false
                                    emailTrovate = resp.emails
                                    if (resp.emails.isEmpty()) errorMessage = resp.message ?: "Nessun account trovato."
                                }
                            } else if (step == 1) {
                                if (email.isBlank()) {
                                    errorMessage = "Inserisci un'email."
                                    return@Button
                                }
                                isLoading = true
                                scope.launch {
                                    val resp = viewModel.requestPasswordReset(email)
                                    isLoading = false
                                    if (resp.success) {
                                        infoMessage = resp.message ?: "Codice inviato, controlla la tua email."
                                        step = 2
                                    } else {
                                        errorMessage = resp.message ?: "Errore durante l'invio del codice."
                                    }
                                }
                            } else {
                                if (code.isBlank() || newPassword.isBlank()) {
                                    errorMessage = "Compila codice e nuova password."
                                    return@Button
                                }
                                if (newPassword != confirmPassword) {
                                    errorMessage = "Le due password non coincidono."
                                    return@Button
                                }
                                isLoading = true
                                scope.launch {
                                    val resp = viewModel.confirmPasswordReset(email, code, newPassword)
                                    isLoading = false
                                    if (resp.success) {
                                        infoMessage = "Password aggiornata, ora puoi accedere."
                                        onDismiss()
                                    } else {
                                        errorMessage = resp.message ?: "Codice non valido o scaduto."
                                    }
                                }
                            }
                        }
                    ) {
                        if (isLoading) {
                            CircularProgressIndicator(modifier = Modifier.size(18.dp), strokeWidth = 2.dp, color = Color.White)
                        } else {
                            Text(
                                when {
                                    step == 1 && cercaEmail -> "Cerca"
                                    step == 1 -> "Invia codice"
                                    else -> "Reimposta password"
                                }
                            )
                        }
                    }
                }
            }
        }
    }
}

/**
 * "Ho perso l'accesso": non si ricorda la password e non si legge più l'email dell'iscrizione.
 * La richiesta va all'amministratore, che verifica l'identità (es. telefonando al numero già
 * registrato) e cambia l'email: lì arriva poi il codice per la nuova password, valido 24 ore.
 */
@Composable
fun RecuperoAccessoDialog(viewModel: AuthViewModel, codiceIniziale: String, onIndietro: () -> Unit, onChiudi: () -> Unit) {
    var codice by remember { mutableStateOf(codiceIniziale.trim()) }
    var numeroOrdine by remember { mutableStateOf("") }
    var telefono by remember { mutableStateOf("") }
    var nuovaEmail by remember { mutableStateOf("") }
    var messaggio by remember { mutableStateOf("") }
    var errore by remember { mutableStateOf<String?>(null) }
    var inviata by remember { mutableStateOf<String?>(null) }
    var isLoading by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    Dialog(onDismissRequest = onChiudi) {
        Card(shape = RoundedCornerShape(16.dp), modifier = Modifier.padding(8.dp)) {
            Column(modifier = Modifier.padding(24.dp).width(320.dp).verticalScroll(rememberScrollState())) {
                Text("Hai perso l'accesso", style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Bold)
                Spacer(modifier = Modifier.height(12.dp))

                if (inviata != null) {
                    Text(inviata!!, style = MaterialTheme.typography.bodyMedium)
                    Spacer(modifier = Modifier.height(20.dp))
                    Button(onClick = onChiudi, modifier = Modifier.align(Alignment.End)) { Text("Chiudi") }
                } else {
                    Text(
                        "Se non ricordi la password e non puoi più leggere la tua email, mandaci questi dati: " +
                            "ti contatteremo per verificare che sei tu e poi cambieremo l'email del tuo account.",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    Spacer(modifier = Modifier.height(12.dp))
                    val campo = Modifier.fillMaxWidth().padding(bottom = 8.dp)
                    OutlinedTextField(codice, { codice = it }, label = { Text("Codice fiscale o partita IVA *") }, singleLine = true, modifier = campo, shape = RoundedCornerShape(12.dp))
                    OutlinedTextField(numeroOrdine, { numeroOrdine = it }, label = { Text("N. iscrizione all'Ordine") }, singleLine = true, modifier = campo, shape = RoundedCornerShape(12.dp))
                    OutlinedTextField(
                        telefono, { telefono = it }, label = { Text("Telefono per ricontattarti *") }, singleLine = true,
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone), modifier = campo, shape = RoundedCornerShape(12.dp)
                    )
                    OutlinedTextField(
                        nuovaEmail, { nuovaEmail = it }, label = { Text("Nuova email *") }, singleLine = true,
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email), modifier = campo, shape = RoundedCornerShape(12.dp)
                    )
                    OutlinedTextField(messaggio, { messaggio = it }, label = { Text("Note (facoltative)") }, modifier = campo, shape = RoundedCornerShape(12.dp))

                    errore?.let {
                        Text(it, color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodySmall)
                    }
                    Spacer(modifier = Modifier.height(12.dp))
                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                        TextButton(onClick = onIndietro) { Text("Indietro") }
                        Spacer(modifier = Modifier.width(8.dp))
                        Button(
                            enabled = !isLoading,
                            onClick = {
                                errore = null
                                if (codice.isBlank() || telefono.isBlank() || nuovaEmail.isBlank()) {
                                    errore = "Compila i campi con l'asterisco."
                                    return@Button
                                }
                                isLoading = true
                                scope.launch {
                                    val resp = viewModel.chiediRecuperoAccesso(
                                        RecuperoAccessoRequest(codice, numeroOrdine.ifBlank { null }, telefono, nuovaEmail, messaggio)
                                    )
                                    isLoading = false
                                    if (resp.success) inviata = resp.message ?: "Richiesta inviata."
                                    else errore = resp.message ?: "Invio non riuscito, riprova."
                                }
                            }
                        ) {
                            if (isLoading) CircularProgressIndicator(modifier = Modifier.size(18.dp), strokeWidth = 2.dp, color = Color.White)
                            else Text("Invia richiesta")
                        }
                    }
                }
            }
        }
    }
}
